/**
 * @render react
 * @name Modal
*/

import React, { PureComponent } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';
import { withAmp } from 'Components/protons/withAmp';

import { Button } from 'Components/atoms/Button';

import renderAmp from './amp';

import './_style.scss';

/**
 *  Modal component
 *
 * @param {boolean} isOpen - flag to open/close the modal
 * @param {String|Component} header - string or component to be rendered as Modal Header
 * @param {String|Component} body - string or component to be rendered as Modal Body
 * @param {String|Component} footer - string or component to be rendered as Modal Footer
 * @param {func} closeModal - function to be called to close the modal
 * @param {func} onClose - function to be called onRequestClose
 * @param {func} onOpen - function to be called onAfterOpen
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - extra classes to be applied to the component
 * @param {Object} context - extra information coming from Polaris provider
 */
class Modal extends PureComponent {
  constructor(props) {
    super(props);

    // To prevent errors from server-side rendering
    // For more information view: https://github.com/reactjs/react-modal/issues/580
    if (typeof window !== 'undefined') {
      ReactModal.setAppElement('body');
    }
  }

  amp() {
    return renderAmp.bind(this)();
  }

  render() {
    const {
      body,
      context: {
        cssPrefix,
      },
      extraClassNames,
      footer,
      header,
      isOpen,
      loadStyles,
      onClose,
      onOpen,
      overlayExtraClassNames,
    } = this.props;

    if (!isOpen) return null;
    return (
      <div className={`${cssPrefix}__modal--container`}>
        <ReactModal
          isOpen={isOpen}
          onAfterOpen={onOpen}
          onRequestClose={onClose}
          shouldCloseOnEsc
          className={`${cssPrefix}__modal--content`}
          overlayClassName={classNames({
            [`${cssPrefix}__modal--overlay`]: true,
            ...overlayExtraClassNames,
          })}
        >
          <div
            className={classNames({
              [`${cssPrefix}__modal`]: loadStyles,
              ...extraClassNames,
            })}
          >
            {header && (
              <div className={`${cssPrefix}__modal--header`}>{header}</div>
            )}
            {body && (
              <div className={`${cssPrefix}__modal--body`}>{body}</div>
            )}
            {footer && (
              <div className={`${cssPrefix}__modal--footer`}>{footer}</div>
            )}

            <Button extraClassNames={{ [`${cssPrefix}__modal--close`]: true }} onClick={onClose}>Close</Button>
          </div>
        </ReactModal>
      </div>
    );
  }
}

Modal.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  ampScripts: PropTypes.shape(),
  // eslint-disable-next-line react/no-unused-prop-types
  ampId: PropTypes.string,
  isOpen: PropTypes.bool,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  overlayExtraClassNames: PropTypes.objectOf(PropTypes.bool),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
    platform: PropTypes.string,
  }),
};

Modal.defaultProps = {
  ampScripts: {
    'amp-lightbox': 'https://cdn.ampproject.org/v0/amp-lightbox-0.1.js',
  },
  ampId: 'amp-lightbox',
  isOpen: false,
  header: '',
  body: '',
  footer: '',
  onClose: () => {},
  onOpen: () => {},
  loadStyles: true,
  extraClassNames: {},
  overlayExtraClassNames: {},
  context: {},
};

export default compose(withPolaris, withAmp)(Modal);
